
import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";
import GradientBorder from "examples/GradientBorder";

// Vision UI Dashboard assets
import radialGradient from "assets/theme/functions/radialGradient";
import palette from "assets/theme/base/colors";
import borders from "assets/theme/base/borders";

// Authentication layout components
import CoverLayout from "layouts/authentication/components/CoverLayout";

// Supabase client
import { supabase } from "../../../supabaseClient"; // Ajuste o caminho se necessário
import { api } from "services/api";

// Images
import bgSignIn from "assets/images/signInImage.png";

// Material-UI components for alerts
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';

function UpdatePassword() {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event) => {
      if (event === "PASSWORD_RECOVERY") {
        // Se for um evento de recuperação de senha, deslogar o usuário imediatamente
        // para que ele possa definir a nova senha sem estar logado com a sessão temporária.
        const { error: signOutError } = await supabase.auth.signOut();
        if (signOutError) {
          console.error("Erro ao deslogar após PASSWORD_RECOVERY:", signOutError);
          setError("Erro interno. Tente novamente.");
        } else {
          console.log("Usuário deslogado após PASSWORD_RECOVERY para redefinição de senha.");
          // Opcional: Limpar a URL para remover os tokens após o signOut
          // window.history.replaceState({}, document.title, window.location.pathname);
        }
      } else {
        // Se não for um evento de recuperação de senha, e o usuário estiver logado,
        // talvez redirecionar para a página de login ou dashboard, dependendo do fluxo.
        // A linha abaixo está comentada, mas é um exemplo de como você poderia lidar com outros eventos.
        // if (event === "SIGNED_IN" || event === "INITIAL_SESSION") {
        //   navigate("/dashboard"); // Exemplo: redirecionar se já estiver logado
        // }
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (newPassword !== confirmPassword) {
      setError("As senhas não coincidem.");
      return;
    }

    if (newPassword.length < 8) {
      setError("A senha deve ter pelo menos 8 caracteres.");
      return;
    }

    setLoading(true);

    const { error: updateError } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (updateError) {
      setError(`Erro ao atualizar a senha: ${updateError.message}`);
    } else {
      try {
        const { data: userData } = await supabase.auth.getUser();
        const email = userData?.user?.email || null;
        await api.post('/webhooks/password-reset/complete', { email });
      } catch (logError) {
        console.warn('Falha ao registrar conclusão de reset de senha:', logError);
      }
      setMessage("Senha atualizada com sucesso! Você já pode fazer login com sua nova senha.");
      setTimeout(() => navigate("/authentication/sign-in"), 3000);
    }

    setLoading(false);
  };

  return (
    <CoverLayout
      title="Crie sua Nova Senha"
      color="white"
      description="Escolha uma senha forte e segura para proteger sua conta."
      premotto=""
      mottoLine1="SEU LANÇAMENTO PROTEGIDO:"
      mottoLine2="SAFERDOCK"
      image={bgSignIn}
    >
      <VuiBox component="form" role="form" onSubmit={handleUpdatePassword}>
        <VuiBox mb={2}>
          <VuiBox mb={1} ml={0.5}>
            <VuiTypography component="label" variant="button" color="white" fontWeight="medium">
              Nova Senha
            </VuiTypography>
          </VuiBox>
          <GradientBorder
            minWidth="100%"
            padding="1px"
            borderRadius={borders.borderRadius.lg}
            backgroundImage={radialGradient(
              palette.gradients.borderLight.main,
              palette.gradients.borderLight.state,
              palette.gradients.borderLight.angle
            )}
          >
            <VuiInput type="password" placeholder="Digite a nova senha..." fontWeight="500" onChange={(e) => setNewPassword(e.target.value)} required />
          </GradientBorder>
        </VuiBox>

        <VuiBox mb={2}>
          <VuiBox mb={1} ml={0.5}>
            <VuiTypography component="label" variant="button" color="white" fontWeight="medium">
              Confirmar Nova Senha
            </VuiTypography>
          </VuiBox>
          <GradientBorder
            minWidth="100%"
            padding="1px"
            borderRadius={borders.borderRadius.lg}
            backgroundImage={radialGradient(
              palette.gradients.borderLight.main,
              palette.gradients.borderLight.state,
              palette.gradients.borderLight.angle
            )}
          >
            <VuiInput type="password" placeholder="Confirme a nova senha..." fontWeight="500" onChange={(e) => setConfirmPassword(e.target.value)} required />
          </GradientBorder>
        </VuiBox>

        {message && (
          <VuiBox mb={2}>
            <Alert severity="success">{message}</Alert>
          </VuiBox>
        )}
        {error && (
          <VuiBox mb={2}>
            <Alert severity="error">{error}</Alert>
          </VuiBox>
        )}

        <VuiBox mt={4} mb={1}>
          <VuiButton type="submit" color="info" fullWidth disabled={loading}>
            {loading ? <CircularProgress size={24} color="inherit" /> : "ATUALIZAR SENHA"}
          </VuiButton>
        </VuiBox>

        <VuiBox mt={3} textAlign="center">
          <VuiTypography variant="button" color="text" fontWeight="regular">
            Voltar para o{" "}
            <VuiTypography
              component={Link}
              to="/authentication/sign-in"
              variant="button"
              color="white"
              fontWeight="medium"
            >
              Login
            </VuiTypography>
          </VuiTypography>
        </VuiBox>
      </VuiBox>
    </CoverLayout>
  );
}

export default UpdatePassword;
